import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { SelectOption } from "./SelectOption";

interface SelectProps<T> {
    placeholder?: string;
    value: SelectValue<T> | null;
    options: SelectValue<T>[];
    disabled?: boolean;
    handleSelection: (selectedIndex: number) => void;
}

export type SelectValue<T> = {
    label: string;
    data: T;
};

export const Select = <T extends unknown>({
    placeholder,
    value,
    options,
    handleSelection,
    disabled = false,
}: SelectProps<T>): JSX.Element => {
    const styles = useDynamicStyles(generateStyles);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const handleClick = () => {
        if (disabled) {
            return;
        }
        setIsOpen(!isOpen);
    };

    const optionClicked = (selectedIndex: number) => {
        setCurrentIndex(selectedIndex);
        handleClick();
        handleSelection(selectedIndex);
    };

    return (
        <Pressable
            style={[styles.select, isOpen ? styles.activeSelect : {}]}
            onPress={() => handleClick()}
            disabled={disabled}
        >
            <Text style={styles.value}>{value ? value.label : placeholder}</Text>
            <svg
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                className={`select-arrow ${isOpen ? "open" : ""}`}
                fill={EStyleSheet.value(GlobalStyles.DROPDOWN_TEXT)}
            >
                <path
                    d="M8.12 9.29L12 13.17l3.88-3.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-4.59
                4.59c-.39.39-1.02.39-1.41 0L6.7 10.7c-.39-.39-.39-1.02 0-1.41.39-.38 1.03-.39 1.42 0z"
                />
            </svg>

            {isOpen && (
                <View style={styles.options}>
                    <View style={styles.content}>{createDropdownOptions(options, optionClicked, currentIndex)}</View>
                </View>
            )}
        </Pressable>
    );
};

const createDropdownOptions = <T extends unknown>(
    options: SelectValue<T>[],
    handleSelection: (value: number) => void,
    currentIndex: number,
): JSX.Element[] => {
    const optionElements: JSX.Element[] = [];
    for (let i = 0; i < options.length; ++i) {
        optionElements.push(
            <SelectOption
                key={i}
                onClick={() => handleSelection(i)}
                label={options[i].label}
                selected={i === currentIndex}
            />,
        );
    }

    return optionElements;
};

const generateStyles = () => {
    return EStyleSheet.create({
        select: {
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            backgroundColor: () => EStyleSheet.value(GlobalStyles.DROPDOWN_BACKGROUND),
            color: () => EStyleSheet.value(GlobalStyles.DROPDOWN_TEXT),
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: 30,
        },
        activeSelect: {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
        value: {
            width: "100%",
            paddingHorizontal: 15,
            color: () => EStyleSheet.value(GlobalStyles.DROPDOWN_TEXT),
            fontSize: 14,
            fontWeight: "800",
        },
        options: {
            top: 30,
            position: "absolute",
            width: "100%",
            maxHeight: 200,
            overflowY: "auto",
            paddingVertical: 5,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.DROPDOWN_OPTIONS_BACKGROUND),
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.DROPDOWN_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.DROPDOWN_BORDER_RADIUS),
        },
        content: {},
    });
};
